import type { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { WallMountOptions } from "../../RackTypes"
import { getRackModel } from "../../modelPaths"
import { AdjustOnlyVisual } from "../../constants"

export class WallmountBuilder {
  protected config: WallMountOptions
  protected line: WallMountOptions['line']
  protected depth: WallMountOptions['depth']
  protected color: WallMountOptions['color']
  protected numbered: WallMountOptions['numbered']
  protected stringer: WallMountOptions['stringer']
  protected jcup: WallMountOptions['jcup']
  protected isRM: boolean
  protected isRML: boolean
  protected material: MaterialInfo

  constructor(config: WallMountOptions) {
    this.config = config
    this.line = config.line
    this.depth = config.depth
    this.color = config.color
    this.numbered = config.numbered
    this.stringer = config.stringer
    this.jcup = config.jcup
    this.isRM = config.line === 'RM'
    this.isRML = config.line === 'RML'
    
    const usesPowdercoat = config.isRML3W || config.is90SlimRack 
    this.material = this.createMaterial(!!usesPowdercoat)
  }

  public build(): PartDefinition {
    return this.assembleWallmount()
  }

  protected createMaterial(usesPowdercoat: boolean): MaterialInfo {
    // Special case: satin clear
    if (this.color === 'satinClear' || this.color === 'powdercoat' ) return { main: { material: this.color }, hardware: { material: this.config.hardware } }
    
    // Standard case
    return {
      main: {
        name     : 'metal',
        material : usesPowdercoat ? 'powdercoat' : 'mg',
        color    : this.color,
      },
      hardware: { material: this.config.hardware },
    }
  }

  protected getCrossmemberMaterial(): MaterialInfo {
    return this.isRM 
      ? { cross: { material: 'mg', color: this.color }, hardware: { material: this.config.hardware } }
      : this.material
  }

  protected createStringer(left: boolean): PartDefinition[] {
    const stringer: PartDefinition[] = []

    if (left) {
      stringer.push({
        mesh   : getRackModel('WM_SHARED', 'stringer'),
        slots  : {},
        anchor : 'left',
        offset : { x: -2.25 },
      })
    }

    return stringer
  }

  protected getTopCrossmemberOffset() {
    if (this.isRM) return { z: -0.4 }
    
    return { z: 0.21 }
  }

  protected getBottomCrossmemberOffset() {
    if (this.isRM) return { z: -0.4 }
    
    return { z: 0.21 }
  }

  protected createTopCrossmember(left: boolean): PartDefinition {
    return {
      mesh  : getRackModel(`WM_${this.line}_PARTS`, `${this.line} folding back crossmember ${this.depth}`),
      slots : {
        back: {
          mesh   : getRackModel('WM_SHARED', 'hinge_assembly'),
          slots  : this.stringer ? { left: this.createStringer(left) } : {},
          anchor : this.isRM ? 'bolt' : '',
        },
      },
      materials : this.getCrossmemberMaterial(),
      anchor    : 'top',
      offset    : this.getTopCrossmemberOffset(),
    }
  }

  protected createBottomCrossmember(left: boolean): PartDefinition {
    return {
      mesh  : getRackModel(`WM_${this.line}_PARTS`, `${this.line} folding back crossmember ${this.depth}`),
      slots : {
        back: {
          mesh   : getRackModel('WM_SHARED', 'hinge_assembly'),
          slots  : this.stringer ? { left: this.createStringer(left) } : {},
          anchor : this.isRM ? 'bolt' : '',
        },
      },
      materials : this.getCrossmemberMaterial(),
      anchor    : 'top',
      offset    : this.getBottomCrossmemberOffset(),
    }
  }

  protected createBrandingSticker(left: boolean): PartDefinition {
    return {
      mesh        : getRackModel(`WM_${this.line}_PARTS`, 'upright sticker'),
      slots       : {},
      adjustments : left ? [] : [{ type: 'flip', axis: 'Z' }],
    }
  }

  protected createJCup(left: boolean): PartDefinition {
    return {
      mesh        : getRackModel(`${this.line}_JCUPS`, `${this.jcup}`),
      slots       : {},
      adjustments : left ? [] : [
        { type: 'rotate', axis: 'Z', amount: 180 }, 
        { type: 'flip', axis: 'Y' },
      ],
    }
  }

  protected createStick(left: boolean): PartDefinition {
    const stick: PartDefinition = {
      mesh: this.numbered
        ? getRackModel(`WM_${this.line}_PARTS`, `${this.line} stick numbered`)
        : getRackModel(`WM_${this.line}_PARTS`, `${this.line} stick`),
      slots: {
        'crossmember-bottom': this.createBottomCrossmember(left),
      },
      materials      : this.material,
      offset         : this.isRM ? { x: 0.3 } : { x: 0.5 },
      adjustments    : left ? [] : [{ type: 'rotate', axis: 'Y', amount: 180 }],
      adjustmentType : AdjustOnlyVisual,
    }

    // Add top crossmember - slot name differs by line
    if (this.isRM) {
      stick.slots['crossmember-top'] = this.createTopCrossmember(left)
    } else {
      stick.slots['top-folding-crossmember'] = this.createTopCrossmember(left)
    }

    // Add branding sticker to left stick only
    if (left && this.isRML) {
      stick.slots['sticker-left'] = this.createBrandingSticker(true)
    }

    // Add J-Cup
    stick.slots['jcup-high'] = this.createJCup(left)

    return stick
  }

  protected createSide(left: boolean): PartDefinition {
    return this.createStick(left)
  }

  protected getPullupMesh(): string {
    return getRackModel(`WM_${this.line}_PARTS`, `wallmount ${this.line} pullup`)
  }

  protected getPullupOffset() {
    if (this.isRM) return { y: -6, x: 0.5 }
    
    return {}
  }

  protected getPullupAdjustments() {
    return []
  }

  protected createPullupBar(): PartDefinition {
    return {
      mesh        : this.getPullupMesh(),
      slots       : {},
      anchor      : 'pin',
      adjustments : this.getPullupAdjustments(),
      offset      : this.getPullupOffset(),
    }
  }

  protected attachPullupBar(leftSide: PartDefinition): void {
    leftSide.slots.top = this.createPullupBar()
  }

  // MARK: Build Aseembly
  protected assembleWallmount(): PartDefinition {
    const leftSide = this.createSide(true)
    const rightSide = this.createSide(false)
    
    // Connect the sides
    leftSide.slots['other-side'] = rightSide
    
    // Add pullup bar
    this.attachPullupBar(leftSide)
    
    return leftSide
  }
}